import { CheckCircleOutline, WarningOutlined } from '@mui/icons-material';
import { styled, Typography } from '@mui/material';
import { ActionOperationType, ActionRef } from 'further-types/lp/action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Row } from 'further-ui/components/Layout';
import { numberToCurrencyString } from 'further-ui/utils';

import { round, sumBy } from 'lodash';

const Totals = styled(Row)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  padding: theme.spacing(theme.spacingSizes.xs, theme.spacingSizes.md),
}));

type Props = {
  bankOperationAmountTcy: number;
  selectedTransactions: Array<{
    id: string;
    splitFromTransactionId?: string;
    unreconciledAmountTcy: number;
    operationType: ActionOperationType;
    currencyCode: CurrencyCode;
    actionRef: ActionRef;
  }>;
  currency: CurrencyCode;
};

const SingleCurrencyReview: React.FC<Props> = ({
  selectedTransactions,
  bankOperationAmountTcy,
  currency,
}) => {
  const sumOfSelectedTransactions = round(
    sumBy(selectedTransactions, (transaction) =>
      transaction.operationType === ActionOperationType.Spent
        ? -transaction.unreconciledAmountTcy
        : transaction.unreconciledAmountTcy,
    ),
    3,
  );

  const amountMatches = sumOfSelectedTransactions === bankOperationAmountTcy;
  const amountDiff = Math.abs(
    sumOfSelectedTransactions - bankOperationAmountTcy,
  );

  const Icon = amountMatches ? CheckCircleOutline : WarningOutlined;

  return (
    <>
      <Typography variant="h5">1. Review selected transactions</Typography>
      <Row spacing="md" fullWidth centered>
        <Totals fullWidth centered justify="space-between">
          <Typography variant="body2">Bank statement line</Typography>
          <Typography variant="body2" fontWeight="bold">
            {numberToCurrencyString(bankOperationAmountTcy, {
              fallback: '-',
              currency,
            })}
          </Typography>
        </Totals>
        <Totals fullWidth centered justify="space-between">
          <Typography variant="body2">Selected transactions</Typography>
          <Typography variant="body2" fontWeight="bold">
            {numberToCurrencyString(sumOfSelectedTransactions, {
              currency,
            })}
          </Typography>
        </Totals>
      </Row>
      <Row centered justify="flex-end" spacing="xs">
        <Icon color={amountMatches ? 'primary' : 'error'} />
        <Typography
          variant="body2"
          color={amountMatches ? 'primary' : 'error'}
          fontWeight="bold"
        >
          {amountMatches
            ? 'Total matches'
            : `Total is out by ${numberToCurrencyString(amountDiff, {
                currency,
              })}`}
        </Typography>
      </Row>
    </>
  );
};

export default SingleCurrencyReview;
